import { Dispatch } from 'react';

import { IFilterKeyValue } from 'common/api/type-def';
import { ISelectOption as IOption } from 'common/components/MulltipleSelectCheckmarks/MultipleSelectCheckmarks';
import { ISelectOption } from 'common/components/Select/SelectDropdown';
import { MODULES } from 'common/constants';
import {
	PROGRESS_BAR_ELEMENTS,
	FILTER_TYPE,
	FILTER_NAME,
} from 'common/constants/CitationDetails';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	REG_INV_GROUP_SEARCH_MAP,
	REG_INV_REGULATION_SEARCH_MAP,
	REG_INV_REGULATION_SEARCH_TYPE,
	REG_INV_CARD_NAMES,
	REG_INV_FILTER_NAME,
	REG_INV_EXPORT_COLUMN_NAMES,
} from 'common/constants/RegulationInventoryConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { TAXONOMY_TAB_VALUES } from 'common/constants/TaxonomyConstants';
import { appPages } from 'common/constants/TextContent';
import { TOAST_MESSAGE_STATUS } from 'common/constants/ToastMessageConstants';
import { LoggedInRole } from 'common/constants/UserManagementConstants';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { pascalFormat, toSentenceCase } from 'common/utils/utilFunctions';

import { getAllUserAndGroupsDetails } from './api';
import { ICitationDetailsElements, IExportDataResponse } from './api/type-def';
import { ICustomBreadcrumbsProps } from './Components/RegulationBreadcrumb/RegulationBreadcrumbs';

const { REACT_APP_ENVIRONMENT } = process.env;

const { GROUP } = REG_INV_REGULATION_SEARCH_TYPE;

const {
	ROLE_APPLICABILITY_LEGAL_MANAGER,
	ROLE_MAPPING_MANAGER,
	ROLE_SUMMARY_MANAGER,
	ROLE_GROUPING_MANAGER,
	ROLE_APPLICABILITY_ANALYST,
	ROLE_MAPPING_ANALYST,
	ROLE_SUMMARY_ANALYST,
	ROLE_GROUPING_ANALYST,
} = LoggedInRole;

const {
	GROUP_NAME,
	SUMMARY,
	GROUP_APPROVED_BY,
	SUMMARY_APPROVED_BY,
	BOOK_TITLE,
	ISSUER,
	JURISDICTION_EXPORT,
	GROUP_APPROVED_ON,
	SUMMARY_MODIFIED_DATE,
	PARAGRAPH_ID,
	THEME_LIST,
	SOURCE_LINK,
} = REG_INV_EXPORT_COLUMN_NAMES;

const {
	NOTSTARTED,
	GROUPING_ASSESSMENT,
	APPLICABILITY_ASSESSMENT,
	GROUPING_REVIEW,
	APPLICABILITY_REVIEW,
	SUMMARY_ASSESSMENT,
	SUMMARY_REVIEW,
	MAPPING_ASSESSMENT,
	MAPPING_REVIEW,
	COMPLETE,
} = PROGRESS_BAR_ELEMENTS;

const { PROCESSES_VALUE } = TAXONOMY_TAB_VALUES;

const { OK, CREATED } = HTTP_STATUS_CODES;
const { TOAST_SUCCESS, TOAST_ERROR } = TOAST_MESSAGE_STATUS;

const {
	CREATED_BY_TYPE,
	THEME_TYPE,
	REG_SECTION_TYPE,
	JURISDICTION_TYPE,
	OWNER_TYPE,
	PRIORITY_TYPE,
} = FILTER_TYPE;

const { FEDERAL_STATES_NAME } = FILTER_NAME;

const { WORK_FLOW_STATUS_TYPE } = FILTER_TYPE;

const {
	FEDERAL_REGULATIONS,
	FEDERAL_LAWS,
	FEDERAL_GUIDANCE,
	STATE_REGULATIONS,
	STATE_LAWS,
	STATE_GUIDANCE,
	INTERNATIONAL_REGULATIONS,
	INTERNATIONAL_LAW,
	INTERNATIONAL_GUIDANCE,
	INDUSTRY_GUIDANCE,
} = REG_INV_CARD_NAMES;

const {
	JURISDICTION,
	THEME_NAME,
	SECTION_NAME,
	ASSIGNED_BY,
	CREATED_BY,
	PRIORITY,
} = REG_INV_REGULATION_SEARCH_MAP;

const {
	JURISDICTION_GROUP,
	THEME_NAME_GROUP,
	SECTION_NAME_GROUP,
	ASSIGNED_BY_GROUP,
	CREATED_BY_GROUP,
	PRIORITY_GROUP,
} = REG_INV_GROUP_SEARCH_MAP;

const { US_STATES, INTERNATIONAL } = REG_INV_FILTER_NAME;

const { RISK_REFERENCE_TAXONOMY } = MODULES;
const { prcInventory, regulationInventory, overView } = rrcpDashboardRoutes;

export enum OPERATION {
	ADD = 'add',
	REMOVE = 'remove',
}

const { PRC_INVENTORY_PAGE } = appPages;
export const getAllCitationsForSummaray = (
	nodes: ICitationDetailsElements,
	result: string,
) => {
	if (nodes) {
		if (nodes.summary) {
			result += nodes.summary as string;
		}
		if (nodes.description) {
			result += nodes.description;
		}
		if (nodes?.children) {
			nodes?.children?.forEach((childObj: any) => {
				result = getAllCitationsForSummaray(childObj, result);
			});
		}
	}
	return result;
};

export const fetchSummarayText = (grouping: ICitationDetailsElements[]) => {
	let result = '';
	grouping.forEach((node) => {
		result += getAllCitationsForSummaray(node, result);
	});
	return result;
};

export const getKeysForRegInvSearch = (
	filterData: IFilterKeyValue[],
	type: string,
) =>
	filterData.map((elem: IFilterKeyValue) => ({
		...elem,
		key:
			type === GROUP
				? mapGroupSearchFieldValues(elem.key)
				: mapRegulationSearchFieldValues(elem.key),
	}));

const mapGroupSearchFieldValues = (key: string) => {
	let filterKey = '';
	switch (key) {
		case THEME_TYPE:
			filterKey = THEME_NAME_GROUP;
			break;
		case REG_SECTION_TYPE:
			filterKey = SECTION_NAME_GROUP;
			break;
		case JURISDICTION_TYPE:
			filterKey = JURISDICTION_GROUP;
			break;
		case OWNER_TYPE:
			filterKey = ASSIGNED_BY_GROUP;
			break;
		case CREATED_BY_TYPE:
			filterKey = CREATED_BY_GROUP;
			break;
		case PRIORITY_TYPE:
			filterKey = PRIORITY_GROUP;
			break;
		default:
			filterKey = key;
			break;
	}
	return filterKey;
};

export const mapRegulationSearchFieldValues = (key: string) => {
	let filterKey = '';
	switch (key) {
		case THEME_TYPE:
			filterKey = THEME_NAME;
			break;
		case REG_SECTION_TYPE:
			filterKey = SECTION_NAME;
			break;
		case JURISDICTION_TYPE:
			filterKey = JURISDICTION;
			break;
		case OWNER_TYPE:
			filterKey = ASSIGNED_BY;
			break;
		case CREATED_BY_TYPE:
			filterKey = CREATED_BY;
			break;
		case PRIORITY_TYPE:
			filterKey = PRIORITY;
			break;
		default:
			filterKey = key;
			break;
	}
	return filterKey;
};

export const getRoleName = (status: string) => {
	switch (status) {
		case NOTSTARTED:
			return ROLE_APPLICABILITY_ANALYST;
		case APPLICABILITY_ASSESSMENT:
			return ROLE_APPLICABILITY_ANALYST;
		case APPLICABILITY_REVIEW:
			return ROLE_APPLICABILITY_LEGAL_MANAGER;
		case GROUPING_ASSESSMENT:
			return ROLE_GROUPING_ANALYST;
		case GROUPING_REVIEW:
			return ROLE_GROUPING_MANAGER;
		case SUMMARY_ASSESSMENT:
			return ROLE_SUMMARY_ANALYST;
		case SUMMARY_REVIEW:
			return ROLE_SUMMARY_MANAGER;
		case MAPPING_ASSESSMENT:
			return ROLE_MAPPING_ANALYST;
		case MAPPING_REVIEW:
			return ROLE_MAPPING_MANAGER;
		case COMPLETE:
			return ROLE_MAPPING_MANAGER;
		default:
			return '';
	}
};

export enum TAB_VALUES {
	REQUIREMENTS = 'Requirements',
	OBLIGATIONS = 'Obligations',
	EXPORT = 'Export',
}

export enum NO_COLUMN_ERROR_MESSAGE {
	NO_COLUMN_DATA = 'Failed to fetch column data',
}

export enum OBLIGATIONS_GROUP_NAME {
	OBLIGATION = 'obligation',
}

export const getRegInvOwnerData = async (status: string) => {
	const getRole = getRoleName(status);
	if (getRole) {
		const response: any = await getAllUserAndGroupsDetails(
			getRoleName(status) || '',
		);
		if (response?.statusCode === OK) {
			const { responseBody } = response;
			const { users, groups } = responseBody;

			const usersList: ISelectOption[] =
				users?.map((userDetails: any) => {
					const { firstName, lastName, id } = userDetails;
					const displayName = `${pascalFormat(
						firstName?.toLowerCase(),
					)} ${pascalFormat(lastName?.toLowerCase())}`;
					return {
						label: displayName,
						value: id?.toLowerCase(),
						obj: { assignedUserId: id?.toLowerCase() },
					};
				}) || [];
			const groupList: ISelectOption[] =
				groups?.map((groupDetails: any) => ({
					label: groupDetails.groupName,
					value: groupDetails.id?.toLowerCase(),
					obj: { assignedGroupId: groupDetails.id?.toLowerCase() },
				})) || [];
			const tempData = {
				status,
				owners: [...usersList, ...groupList],
			};
			return tempData;
		}
	}
	return null;
};

export const getRegInvRegulationSearchIndexData = (type: string) => {
	let tableIndex = '';
	if (type === GROUP) {
		tableIndex = `reginv-group-index-${process.env.REACT_APP_ENVIRONMENT}`;
	} else {
		tableIndex = `reginv-theme-index-${process.env.REACT_APP_ENVIRONMENT}`;
	}
	return tableIndex;
};

export const getMappedColumnNamesFromResponse = (data: IExportDataResponse) => {
	const {
		groupName,
		summary,
		summaryApprovedBy,
		bookTitle,
		issuer,
		jurisdiction,
		groupApprovedOn,
		groupApprovedBy,
		summaryModifiedDate,
		paragraphId,
		themeList,
		sourceLink,
	} = data;
	const columns: IOption[] = [
		{ label: bookTitle, value: BOOK_TITLE },
		{ label: paragraphId, value: PARAGRAPH_ID },
		{ label: sourceLink, value: SOURCE_LINK },
		{ label: summary, value: SUMMARY },
		{ label: summaryModifiedDate, value: SUMMARY_MODIFIED_DATE },
		{ label: summaryApprovedBy, value: SUMMARY_APPROVED_BY },
		{ label: jurisdiction, value: JURISDICTION_EXPORT },
		{ label: issuer, value: ISSUER },
		{ label: themeList, value: THEME_LIST },
		{ label: groupName, value: GROUP_NAME },
		{ label: groupApprovedBy, value: GROUP_APPROVED_BY },
		{ label: groupApprovedOn, value: GROUP_APPROVED_ON },
	];
	return columns;
};

const getFiltersForExport = (filterData: any) => {
	filterData = filterData.map((elem: IFilterKeyValue) => ({
		...elem,
		key: mapGroupSearchFieldValues(elem.key),
	}));
	if (filterData.length === 0) {
		return [...filterData, { key: WORK_FLOW_STATUS_TYPE, values: [COMPLETE] }];
	}
	return filterData;
};

export const getExportPayloadForRequirementInventory = (
	options: IOption[],
	searchText: string,
	filters: any,
	totalRecordsCount: number,
	exportTypeName: string,
) => {
	const checkedOptions = options
		.filter((res) => res.checked)
		.map((res) => res.value);
	checkedOptions.push(WORK_FLOW_STATUS_TYPE);
	return {
		payload: {
			indexName: `reginv-group-index-${REACT_APP_ENVIRONMENT}`,
			searchText,
			filters: getFiltersForExport(filters),
			totalRecordsCount,
			export: {
				columns: checkedOptions,
				name: exportTypeName,
			},
		},
	};
};

export const relationshipDropDownValues: ISelectOption[] = [
	{
		value: `${PROCESSES_VALUE.charAt(0)}${PROCESSES_VALUE.slice(
			1,
			PROCESSES_VALUE.length,
		)}`,
		label: 'Process',
	},
	{
		value: 'Risks',
		label: 'Risk',
	},
	{
		value: 'Controls',
		label: 'Control',
	},
	{
		value: 'Policies',
		label: 'Policy',
	},
];

export const tableUniqueValues = [
	'AllRegTable',
	'ApplicabilityRegTable',
	'GroupingRegTable',
	'GroupingObRegTable',
	'SummaryRegTable',
	'MappingRegTable',
	'CompletedRegTable',
];

export const industryIssuanceTypes: string[] = ['Rule', 'RuleBook'];

export const regulationIssuanceTypes: string[] = [
	'Regulation',
	'Requirement',
	'Sanction',
];
export const lawIssuanceTypes: string[] = [
	'Act',
	'Decree',
	'Decree Law',
	'Law',
	'Legislation',
	'Statute',
];

export const guidanceIssuaunceTypes: string[] = [
	'Addendum',
	'Administrative Proceeding',
	'Advisory',
	'Agenda',
	'Agreement',
	'Alert',
	'Analysis',
	'Annex',
	'Announcement',
	'Appendix',
	'Article',
	'Best Practice',
	'Blog',
	'Booklet',
	'Brief',
	'Brochure',
	'Bulletin',
	'Bye-law',
	'Catalogue',
	'Circular',
	'Code',
	'Communication',
	'Complaint',
	'Conference',
	'Consultation',
	'Convention',
	'Correction',
	'Data',
	'Decision',
	'Declaration',
	'Deliberation',
	'Determination',
	'Dictionary',
	'Direction',
	'Directive',
	'Discussion Paper',
	'Draft',
	'Email',
	'Enforcement',
	'Event',
	'Excerpt',
	'Explanatory Note',
	'Fact Sheet',
	'Feedback statement',
	'Fine',
	'Form',
	'Framework',
	'Frequently Asked Question',
	'Guidance',
	'Guidance Note',
	'Handbook',
	'Implementation Note',
	'Information Sheet',
	'Instruction',
	'Instrument',
	'Interpretation',
	'Interview',
	'Journal',
	'Judgement',
	'Letter',
	'Litigation Release',
	'Manual',
	'Measure',
	'Memorandum',
	'Model',
	'News',
	'Newsletter',
	'No Action Relief Letter',
	'Notice',
	'Operational Arrangement',
	'Opinion',
	'Order',
	'Ordinance',
	'Overview',
	'Paper',
	'Plan',
	'Policy',
	'Policy Statement',
	'Position',
	'Practice Note',
	'Presidential Document',
	'Press Release',
	'Principle',
	'Procedure',
	'Proposed Rule',
	'Protocol',
	'Provision',
	'Publication',
	'Questionnaire',
	'Recommendation',
	'Regime',
	'Report',
	'Requirement',
	'Research',
	'Resolution',
	'Response',
	'Sanction',
	'Specification',
	'Speech',
	'Standard',
	'Statement',
	'Statistic',
	'Supervisory Letter',
	'Suspension',
	'Technical Standard',
	'Testimony',
	'Warning',
	'White Paper',
	'Working Paper',
];

export const currentInventoryCardDetails = (userId: string) => {
	const cardDetails = [
		{
			title: FEDERAL_REGULATIONS,
			headers: {
				loggedInUser: userId,
				jurisdiction: FEDERAL_STATES_NAME,
				issuanceType: regulationIssuanceTypes,
			},
		},
		{
			title: FEDERAL_LAWS,
			headers: {
				loggedInUser: userId,
				jurisdiction: FEDERAL_STATES_NAME,
				issuanceType: lawIssuanceTypes,
			},
		},
		{
			title: FEDERAL_GUIDANCE,
			headers: {
				loggedInUser: userId,
				jurisdiction: FEDERAL_STATES_NAME,
				issuanceType: guidanceIssuaunceTypes,
			},
		},
		{
			title: STATE_REGULATIONS,
			headers: {
				loggedInUser: userId,
				jurisdiction: US_STATES,
				issuanceType: regulationIssuanceTypes,
			},
		},
		{
			title: STATE_LAWS,
			headers: {
				loggedInUser: userId,
				jurisdiction: US_STATES,
				issuanceType: lawIssuanceTypes,
			},
		},
		{
			title: STATE_GUIDANCE,
			headers: {
				loggedInUser: userId,
				jurisdiction: US_STATES,
				issuanceType: guidanceIssuaunceTypes,
			},
		},
		{
			title: INTERNATIONAL_REGULATIONS,
			headers: {
				loggedInUser: userId,
				jurisdiction: INTERNATIONAL,
				issuanceType: regulationIssuanceTypes,
			},
		},
		{
			title: INTERNATIONAL_LAW,
			headers: {
				loggedInUser: userId,
				jurisdiction: INTERNATIONAL,
				issuanceType: lawIssuanceTypes,
			},
		},
		{
			title: INTERNATIONAL_GUIDANCE,
			headers: {
				loggedInUser: userId,
				jurisdiction: INTERNATIONAL,
				issuanceType: guidanceIssuaunceTypes,
			},
		},
		{
			title: INDUSTRY_GUIDANCE,
			headers: {
				loggedInUser: userId,
				issuanceType: industryIssuanceTypes,
			},
		},
	];
	return cardDetails;
};

export const buildWorkFlowCardDetails = [
	{
		title: FEDERAL_REGULATIONS,
		headers: {
			jurisdiction: FEDERAL_STATES_NAME,
			issuanceType: regulationIssuanceTypes,
		},
	},
	{
		title: FEDERAL_LAWS,
		headers: {
			jurisdiction: FEDERAL_STATES_NAME,
			issuanceType: lawIssuanceTypes,
		},
	},
	{
		title: FEDERAL_GUIDANCE,
		headers: {
			jurisdiction: FEDERAL_STATES_NAME,
			issuanceType: guidanceIssuaunceTypes,
		},
	},
	{
		title: STATE_REGULATIONS,
		headers: {
			jurisdiction: US_STATES,
			issuanceType: regulationIssuanceTypes,
		},
	},
	{
		title: STATE_LAWS,
		headers: {
			jurisdiction: US_STATES,
			issuanceType: lawIssuanceTypes,
		},
	},
	{
		title: STATE_GUIDANCE,
		headers: {
			jurisdiction: US_STATES,
			issuanceType: guidanceIssuaunceTypes,
		},
	},
	{
		title: INTERNATIONAL_REGULATIONS,
		headers: {
			jurisdiction: INTERNATIONAL,
			issuanceType: regulationIssuanceTypes,
		},
	},
	{
		title: INTERNATIONAL_LAW,
		headers: {
			jurisdiction: INTERNATIONAL,
			issuanceType: lawIssuanceTypes,
		},
	},
	{
		title: INTERNATIONAL_GUIDANCE,
		headers: {
			jurisdiction: INTERNATIONAL,
			issuanceType: guidanceIssuaunceTypes,
		},
	},
	{
		title: INDUSTRY_GUIDANCE,
		headers: {
			issuanceType: industryIssuanceTypes,
		},
	},
];

export const getBreadCrumbs = (
	breadCrumbText: string,
	groupName: string,
): ICustomBreadcrumbsProps[] => {
	if (breadCrumbText === RISK_REFERENCE_TAXONOMY) {
		return [
			{
				title: PRC_INVENTORY_PAGE,
				type: 'link',
				key: 'taxonomy',
				path: prcInventory,
				dataTestId: 'taxonomy-breadcrumbs-link',
			},
			{
				title: groupName || '',
				type: 'text',
				key: 'static-text',
			},
		];
	}
	return [
		{
			title: toSentenceCase(breadCrumbText),
			type: 'link',
			key: 'req-inv',
			path: regulationInventory,
			dataTestId: 'inventory-breadcrumbs-link',
		},
		{
			title: '',
			type: 'link',
			key: 'req-inv-selected-tab',
			path: `${regulationInventory}/${overView}`,
			dataTestId: 'inventory-breadcrumbs-link-selected-tab',
		},
		{
			title: groupName || '',
			type: 'text',
			key: 'static-text',
		},
	];
};

/**
 * @description method to fetch the toast msg data
 */
export const fetchToastMsgData = (
	response: any,
	updateAction: Dispatch<any>,
) => {
	if (response?.statusCode === CREATED) {
		updateAction(
			setToastMessage({
				message: 'The mapping has been successfully done',
				severity: TOAST_SUCCESS,
				isToastOpen: true,
			}),
		);
	} else {
		const { errorBody } = response;
		const errorMsg = errorBody?.errorMessage as string;
		updateAction(
			setToastMessage({
				message: errorMsg,
				severity: TOAST_ERROR,
				isToastOpen: true,
			}),
		);
	}
};
